import { BRAND_COLORS } from '@constants/shared-colors';

export const getCSSVar = (variableName: string, fallback?: string) => {
  // Get fresh computed styles each time to pick up dynamic CSS variable changes
  if (typeof window === 'undefined') {
    return fallback || '';
  }

  const rootStyles = window.getComputedStyle(document.documentElement);
  const value = rootStyles.getPropertyValue(variableName).trim();
  // Return fallback if value is empty, otherwise return the resolved value
  return value || fallback || '';
};

// Control border: form controls hover neutral gray; text Inputs focus to the
// primary blue ring, while Select/Picker/Color keep the neutral focus.
const CONTROL_BORDER = '#9f9f9f';
// Shared with the SCSS side via --color-control-border-subtle (generated from the
// same shared-colors source), so antd tokens and flat dashboard chrome never drift.
const CONTROL_BORDER_SUBTLE = BRAND_COLORS.controlBorderSubtle;
const CONTROL_OUTLINE_SHADOW = '0 0 0 2px rgba(159, 159, 159, 0.2)';

// Default fallback values for CSS variables
const defaultColors = {
  '--color-primary': '#3D8FEB',
  '--color-secondary': '#7B7B7B',
  '--color-light100': '#F8F7F7',
  '--color-light200': '#F1F1F1',
  '--color-light400': '#E2E2E2',
  '--color-info': '#3582FF',
  '--color-success': '#49BA61',
  '--color-warning': '#FFB73A',
  '--color-danger': '#FE5938',
  '--color-white': '#FDFDFD',
  '--color-primary-100': '#EFF6FF',
  '--color-primary-200': '#DBEAFE',
  '--font-family': '"Manrope", sans-serif',
  '--color-primary-border': '#3a7cf7',
};

export const cssVars = {
  '--color-primary': 'var(--color-primary)',
  '--color-secondary': 'var(--color-leo-secondary)',
  '--color-light100': 'var(--color-light100)',
  '--color-light200': 'var(--color-light200)',
  '--color-light400': 'var(--color-light400)',
  '--color-info': 'var(--color-info)',
  '--color-success': 'var(--color-success)',
  '--color-warning': 'var(--color-warning)',
  '--color-danger': 'var(--color-danger)',
  '--font-family': '"Manrope", sans-serif',
  '--color-white': 'var(--color-body-text)',
  '--color-primary-100': 'var(--color-primary-100)',
  '--color-primary-200': 'var(--color-primary-200)',
};

export const AntTheme = () => ({
  cssVar: { key: 'leo' },
  hashed: false,
  components: {
    Form: {
      itemMarginBottom: 25,
      labelFontWeight: 500,
    },
    Input: {
      // Controls are bordered only — no fill — so the page/card shows through.
      colorBgContainer: 'transparent',
      hoverBg: 'transparent',
      activeBg: 'transparent',
      colorBorder: CONTROL_BORDER_SUBTLE,
      hoverBorderColor: CONTROL_BORDER,
      // Focus to the primary blue ring (mirrors the old search-field treatment).
      activeBorderColor: getCSSVar('--color-primary', defaultColors['--color-primary']),
      activeOutlineColor: 'color-mix(in srgb, var(--color-primary) 20%, transparent)',
      activeShadow: '0 0 0 2px color-mix(in srgb, var(--color-primary) 25%, transparent)',
    },
    Select: {
      selectorBg: 'transparent',
      optionActiveBg: getCSSVar('--color-light200', defaultColors['--color-light200']),
      optionSelectedBg: 'transparent',
      optionSelectedColor: getCSSVar('--color-primary', defaultColors['--color-primary']),
      optionActiveColor: getCSSVar('--color-primary', defaultColors['--color-primary']),
      colorBorder: CONTROL_BORDER_SUBTLE,
      hoverBorderColor: CONTROL_BORDER,
      activeBorderColor: CONTROL_BORDER,
      activeOutlineColor: 'rgba(159, 159, 159, 0.2)',
      activeShadow: CONTROL_OUTLINE_SHADOW,
    },
    Badge: {
      colorBorderBg: 'transparent',
    },
    Color: {
      colorBorder: CONTROL_BORDER_SUBTLE,
      hoverBorderColor: CONTROL_BORDER,
      activeBorderColor: CONTROL_BORDER,
      activeOutlineColor: 'rgba(159, 159, 159, 0.2)',
      activeShadow: CONTROL_OUTLINE_SHADOW,
    },
    Picker: {
      colorBgContainer: 'transparent',
      colorBorder: CONTROL_BORDER_SUBTLE,
      hoverBorderColor: CONTROL_BORDER,
      activeBorderColor: CONTROL_BORDER,
      activeOutlineColor: 'rgba(159, 159, 159, 0.2)',
      activeShadow: CONTROL_OUTLINE_SHADOW,
    },
    DatePicker: {
      colorBgContainer: 'transparent',
      activeBg: 'transparent',
      activeBorderColor: getCSSVar('--color-secondary', defaultColors['--color-secondary']),
      activeOutlineColor: getCSSVar('--color-light200', defaultColors['--color-light200']),
      activeShadow: CONTROL_OUTLINE_SHADOW,
      hoverBorderColor: getCSSVar('--color-secondary', defaultColors['--color-secondary']),
      hoverOutlineColor: getCSSVar('--color-light200', defaultColors['--color-light200']),
    },
    Checkbox: {
      colorPrimary: getCSSVar('--color-primary', defaultColors['--color-primary']),
      colorWhite: getCSSVar('--color-white', defaultColors['--color-white']),
    },
    Dropdown: {
      controlItemBgActive: getCSSVar('--color-primary', defaultColors['--color-primary']),
      itemSelectedColor: getCSSVar('--color-body-text', defaultColors['--color-white']),
    },
    Switch: {
      colorPrimary: getCSSVar('--color-success', defaultColors['--color-success']),
      trackBg: getCSSVar('--color-light400', defaultColors['--color-light400']),
    },
    Tooltip: {
      fontSize: 11,
      controlHeight: 11,
      marginBottom: 0,
      padding: 0,
    },
    Tree: {
      indentSize: 10,
    },
    Notification: {
      zIndexPopup: 1050,
      width: 384,
      paddingBlock: 16,
      paddingInline: 24,
      borderRadius: 8,
      fontSize: 13,
      fontFamily: getCSSVar('--font-family', defaultColors['--font-family']),
    },
    Pagination: {
      lineWidth: 0,
      itemBg: 'transparent',
      itemActiveBg: getCSSVar('--color-light400', defaultColors['--color-light400']),
      colorPrimary: getCSSVar('--color-body-text', '#121212'),
      colorPrimaryHover: getCSSVar('--color-body-text', '#121212'),
    },
    Popover: {
      boxShadowSecondary: '0 0px 24px rgba(0, 0, 0, 0.2), 0 1px 4px rgba(0, 0, 0, 0.1)',
    },
    Segmented: {
      trackBg: 'transparent',
      trackPadding: 0,
      itemSelectedBg: getCSSVar('--color-light200', defaultColors['--color-light200']),
      itemHoverBg: getCSSVar('--color-light200', defaultColors['--color-light200']),
      boxShadowTertiary: 'none',
      borderRadiusXS: 16,
    },
  },
  token: {
    colorPrimaryBorder: getCSSVar('--color-primary-border', defaultColors['--color-primary-border']),
    colorPrimary: getCSSVar('--color-primary', defaultColors['--color-primary']),
    colorLink: getCSSVar('--color-primary', defaultColors['--color-primary']),
    colorLinkHover: getCSSVar('--color-primary', defaultColors['--color-primary']),
    colorLinkActive: getCSSVar('--color-primary', defaultColors['--color-primary']),
    colorInfo: getCSSVar('--color-info', defaultColors['--color-info']),
    colorSuccess: getCSSVar('--color-success', defaultColors['--color-success']),
    colorWarning: getCSSVar('--color-warning', defaultColors['--color-warning']),
    colorError: getCSSVar('--color-danger', defaultColors['--color-danger']),
    fontFamily: getCSSVar('--font-family', defaultColors['--font-family']),
    fontSize: 13,
    // Slightly softer corners than antd's default 6 across all components. Pin
    // the large variant to the base so `size="large"` controls don't drift off
    // it (antd otherwise derives lg = base+2).
    borderRadius: 8,
    borderRadiusLG: 8,
  },
  Layout: {
    headerHeight: 60,
    headerPadding: 12,
  },
});

export const setCSSVariables = (variables: { [key: string]: string }) => {
  // Iterate over the key-value pairs in the object
  Object.entries(variables).forEach(([variableName, value]) => {
    // Check if the variable already exists
    const rootStyles = window.getComputedStyle(document.documentElement);
    const currentValue = rootStyles.getPropertyValue(variableName).trim();

    // Set the value of the CSS variable only if it's different from the current value
    if (currentValue !== value) {
      document.documentElement.style.setProperty(variableName, value);
    }
  });
};
